/**
 * Utility to handle and prevent ResizeObserver loop errors.
 * This script shims the global ResizeObserver to ensure callbacks are throttled
 * to the next animation frame, which prevents the "loop completed" error.
 */

export const setupErrorHandlers = () => {
  if (typeof window !== "undefined") {
    // 1. Shim ResizeObserver to prevent the error from even occurring
    const RO = window.ResizeObserver;
    if (RO) {
      window.ResizeObserver = class ResizeObserver extends RO {
        constructor(callback: ResizeObserverCallback) {
          super((entries, observer) => {
            window.requestAnimationFrame(() => {
              if (!Array.isArray(entries) || !entries.length) {
                return;
              }
              callback(entries, observer);
            });
          });
        }
      };
    }

    // 2. Suppress the error message globally as a fallback
    const resizeObserverError =
      "ResizeObserver loop completed with undelivered notifications";
    const resizeObserverLimitError = "ResizeObserver loop limit exceeded";

    const handleResizeObserverError = (
      event: ErrorEvent | PromiseRejectionEvent,
    ) => {
      const message =
        event instanceof ErrorEvent ? event.message : event.reason?.message;
      if (
        message &&
        (message.includes(resizeObserverError) ||
          message.includes(resizeObserverLimitError))
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return true;
      }
      return false;
    };

    window.addEventListener("error", handleResizeObserverError, true);
    window.addEventListener(
      "unhandledrejection",
      handleResizeObserverError,
      true,
    );

    // 3. Override console.error as a final safety measure
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
      if (
        args[0] &&
        typeof args[0] === "string" &&
        (args[0].includes(resizeObserverError) ||
          args[0].includes(resizeObserverLimitError))
      ) {
        return;
      }
      originalConsoleError.apply(console, args);
    };
  }
};
